import { Suspense, lazy, useState } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';

import Header from './components/Header.jsx';
import Footer from './components/Footer.jsx';
import Loader from './components/Loader.jsx';
import ScrollToTop from './components/ScrollToTop.jsx';
import ProtectedRoute from './components/ProtectedRoute.jsx';

import Home from './pages/Home.jsx';

/* Interior pages are code-split: the home page is what most visitors see, and
   it shouldn't carry the weight of the admin dashboard. */
const LocationServiceTimes = lazy(() => import('./pages/visiting/LocationServiceTimes.jsx'));
const ShuttleBus = lazy(() => import('./pages/visiting/ShuttleBus.jsx'));
const CalendarPage = lazy(() => import('./pages/news/CalendarPage.jsx'));
const WatchLive = lazy(() => import('./pages/watch/WatchLive.jsx'));
const PastServices = lazy(() => import('./pages/watch/PastServices.jsx'));
const BibleStudy = lazy(() => import('./pages/education/BibleStudy.jsx'));
const CommittedKids = lazy(() => import('./pages/education/CommittedKids.jsx'));
const ClothesCloset = lazy(() => import('./pages/outreach/ClothesCloset.jsx'));
const Give = lazy(() => import('./pages/give/Give.jsx'));
const WaysToGive = lazy(() => import('./pages/give/WaysToGive.jsx'));
const Staff = lazy(() => import('./pages/about/Staff.jsx'));
const VisionMission = lazy(() => import('./pages/about/VisionMission.jsx'));
const WhatWeBelieve = lazy(() => import('./pages/about/WhatWeBelieve.jsx'));
const History = lazy(() => import('./pages/about/History.jsx'));
const Contact = lazy(() => import('./pages/Contact.jsx'));
const Login = lazy(() => import('./pages/auth/Login.jsx'));
const Signup = lazy(() => import('./pages/auth/Signup.jsx'));
const Account = lazy(() => import('./pages/account/Account.jsx'));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard.jsx'));
const NotFound = lazy(() => import('./pages/NotFound.jsx'));

/* Section index pages redirect to their first child, so a parent nav item is
   never a dead end. */

function RouteFallback() {
  return (
    <div className="container section" role="status" aria-live="polite" style={{ minHeight: '50vh' }}>
      <p className="type-body-lg">Loading…</p>
    </div>
  );
}

export default function App() {
  const [booted, setBooted] = useState(false);
  const { pathname } = useLocation();

  // Auth screens get a clean, chrome-free presentation.
  const bareLayout = ['/login', '/signup'].includes(pathname);

  return (
    <>
      {!booted && <Loader onDone={() => setBooted(true)} />}

      <a className="skip-link" href="#main">
        Skip to main content
      </a>

      <ScrollToTop />

      {!bareLayout && <Header />}

      <main id="main">
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/" element={<Home />} />

            {/* Visiting */}
            <Route path="/visiting" element={<Navigate to="/visiting/location-service-times" replace />} />
            <Route path="/visiting/location-service-times" element={<LocationServiceTimes />} />
            <Route path="/visiting/shuttle-bus" element={<ShuttleBus />} />

            {/* News & Events */}
            <Route path="/news-events" element={<Navigate to="/news-events/calendar" replace />} />
            <Route path="/news-events/calendar" element={<CalendarPage />} />

            {/* Watch Online */}
            <Route path="/watch" element={<Navigate to="/watch/live" replace />} />
            <Route path="/watch/live" element={<WatchLive />} />
            <Route path="/watch/past-services" element={<PastServices />} />

            {/* Christian Education */}
            <Route path="/education" element={<Navigate to="/education/bible-study" replace />} />
            <Route path="/education/bible-study" element={<BibleStudy />} />
            <Route path="/education/committed-kids" element={<CommittedKids />} />

            {/* Outreach */}
            <Route path="/outreach" element={<Navigate to="/outreach/clothes-closet" replace />} />
            <Route path="/outreach/clothes-closet" element={<ClothesCloset />} />

            {/* Give */}
            <Route path="/give" element={<Give />} />
            <Route path="/give/ways-to-give" element={<WaysToGive />} />

            {/* About */}
            <Route path="/about" element={<Navigate to="/about/staff" replace />} />
            <Route path="/about/staff" element={<Staff />} />
            <Route path="/about/vision-mission" element={<VisionMission />} />
            <Route path="/about/what-we-believe" element={<WhatWeBelieve />} />
            <Route path="/about/history" element={<History />} />

            <Route path="/contact" element={<Contact />} />

            {/* Auth */}
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />

            <Route
              path="/account"
              element={
                <ProtectedRoute>
                  <Account />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin"
              element={
                <ProtectedRoute requireAdmin>
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />

            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </main>

      {!bareLayout && <Footer />}
    </>
  );
}
